/**
 * useWelcomeAnimation Hook
 *
 * Manages the state machine, timing, audio, and persistence
 * for the theatrical welcome animation.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  AnimationPhase,
  ANIMATION_TIMING,
  STORAGE_KEY,
  ASSETS,
} from "./constants";
import { areCookiesAccepted, COOKIES_ACCEPTED_EVENT } from "../CookieConsent";

interface UseWelcomeAnimationReturn {
  phase: AnimationPhase;
  isVisible: boolean;
  showPersistentSandbag: boolean;
  triggerAnimation: () => void;
  dismissAnimation: () => void;
  prefersReducedMotion: boolean;
}

// Check if user has seen the welcome animation before
function hasSeenWelcome(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

// Mark welcome as seen
function markWelcomeSeen(): void {
  try {
    localStorage.setItem(STORAGE_KEY, "true");
  } catch {
    // Silent fail
  }
}

export function useWelcomeAnimation(): UseWelcomeAnimationReturn {
  const location = useLocation();
  const isLandingPage = location.pathname === "/";

  // Check for reduced motion preference
  const prefersReducedMotion = useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }, []);

  // Determine initial state
  // Animation only starts after cookies are accepted
  const [phase, setPhase] = useState<AnimationPhase>(() => {
    if (!isLandingPage) return "idle";
    const seen = hasSeenWelcome();
    if (seen) return "idle";
    // If cookies already accepted (returning visitor who cleared welcome but not cookies),
    // start dwell. Otherwise wait for cookie acceptance.
    const cookiesOk = areCookiesAccepted();
    return cookiesOk ? "dwell" : "idle";
  });

  const [hasBeenDismissed, setHasBeenDismissed] = useState(hasSeenWelcome());

  // Audio reference - preload on mount
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio(ASSETS.SOUND);
      audioRef.current.preload = "auto";
      audioRef.current.volume = 0.3; // 30% volume
    }
  }, []);

  // Unlock audio for later playback (must be called during user gesture)
  const unlockAudio = useCallback(() => {
    if (audioRef.current) {
      // Play briefly to unlock, then pause and reset
      audioRef.current
        .play()
        .then(() => {
          audioRef.current?.pause();
          if (audioRef.current) audioRef.current.currentTime = 0;
        })
        .catch(() => {
          // Silent fail - will try again on actual play
        });
    }
  }, []);

  // Play sound (with error handling for auto-play blocking)
  const playSound = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {
        // Auto-play blocked - animation continues without sound
      });
    }
  }, []);

  // Listen for cookie acceptance to start animation
  useEffect(() => {
    if (!isLandingPage) return;
    if (hasSeenWelcome()) return; // Already seen, don't auto-play

    const handleCookiesAccepted = () => {
      // Unlock audio during user gesture (cookie accept click)
      // This allows playback later when animation starts
      unlockAudio();
      // Only start if we're still in idle (haven't started yet)
      setPhase((current) => (current === "idle" ? "dwell" : current));
    };

    window.addEventListener(COOKIES_ACCEPTED_EVENT, handleCookiesAccepted);
    return () => {
      window.removeEventListener(COOKIES_ACCEPTED_EVENT, handleCookiesAccepted);
    };
  }, [isLandingPage, unlockAudio]);

  // Reset to idle when navigating away from landing page
  // Without this, if animation is mid-phase (e.g. 'rest') when user navigates to /app,
  // the phase never transitions back to 'idle' and the backdrop stays visible
  useEffect(() => {
    if (!isLandingPage && phase !== "idle") {
      setPhase("idle");
    }
  }, [isLandingPage, phase]);

  // Phase transition logic
  useEffect(() => {
    if (!isLandingPage) return;

    let timeoutId: ReturnType<typeof setTimeout>;

    switch (phase) {
      case "dwell":
        // After dwell, start fly-in
        timeoutId = setTimeout(() => {
          if (prefersReducedMotion) {
            // Skip animation, go directly to rest
            setPhase("rest");
          } else {
            setPhase("fly_in");
            playSound();
          }
        }, ANIMATION_TIMING.DWELL_DURATION);
        break;

      case "fly_in":
        // After fly-in, settle
        timeoutId = setTimeout(() => {
          setPhase("settle");
        }, ANIMATION_TIMING.FLY_DURATION);
        break;

      case "settle":
        // After settle, rest
        timeoutId = setTimeout(() => {
          setPhase("rest");
        }, ANIMATION_TIMING.SETTLE_DURATION);
        break;

      case "fly_out":
        // After fly-out, go to idle
        timeoutId = setTimeout(
          () => {
            setPhase("idle");
            setHasBeenDismissed(true);
            markWelcomeSeen();
          },
          prefersReducedMotion ? 300 : ANIMATION_TIMING.FLY_OUT_DURATION,
        );
        break;
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [phase, isLandingPage, playSound, prefersReducedMotion]);

  // Trigger animation (from persistent sandbag click) - skip dwell, start immediately
  // Only works after animation has been seen at least once
  const triggerAnimation = useCallback(() => {
    if (phase === "idle" && hasBeenDismissed) {
      setPhase("fly_in");
      playSound();
    }
  }, [phase, hasBeenDismissed, playSound]);

  // Dismiss animation (user click/keyboard)
  const dismissAnimation = useCallback(() => {
    if (phase === "rest") {
      if (prefersReducedMotion) {
        // Instant dismiss for reduced motion
        setPhase("fly_out");
      } else {
        setPhase("fly_out");
        playSound();
      }
    }
  }, [phase, playSound, prefersReducedMotion]);

  // Determine visibility states
  const isVisible = phase !== "idle";
  // Show sandbag on landing page whenever animation isn't playing (even before cookies accepted)
  const showPersistentSandbag = isLandingPage && phase === "idle";

  return {
    phase,
    isVisible,
    showPersistentSandbag,
    triggerAnimation,
    dismissAnimation,
    prefersReducedMotion,
  };
}
